/* "<metric> by day": stacked bars with a Y axis, a legend with totals and a
   tooltip per day. Revenue can split by Seller or by Revenue type; chats and
   messages split AI / Team; PPVs sold split AI / Team / Unknown; "Chats that paid"
   is one series. Money days are UTC days, activity days Kyiv days. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatStatsResponse, StatisticsResponse } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon, Seg } from '../../ChatAssistant/compact/kit';
import { HATCH, SELLER_COLOR, fmt, pick, ratio, shortDay, typeColor, typeOrder, usd, type MoneyMode } from './model';

type Metric = 'rev' | 'chats' | 'msgs' | 'sold' | 'conv';
type Split = 'who' | 'type';

interface Series { key: string; label: string; color: string; hatch?: boolean; values: number[] }

interface Props {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  mode: MoneyMode;
  hasCrm: boolean;
}

function niceTop(max: number, rate: boolean) {
  if (max <= 0) return rate ? 10 : 10;
  if (rate) return Math.ceil(max / 5) * 5;
  const step = max > 2000 ? 1000 : max > 200 ? 100 : 10;
  return Math.ceil(max / step) * step;
}

const compactUsd = (v: number) => (v >= 1000 ? `$${(v / 1000).toFixed(1).replace('.0', '')}k` : `$${Math.round(v)}`);
const compactNum = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1).replace('.0', '')}k` : `${Math.round(v)}`);

export function DailyChart({ stats, chat, mode, hasCrm }: Props) {
  const { t } = useTranslation('statsUnified');
  const [metric, setMetric] = useState<Metric>('rev');
  const [split, setSplit] = useState<Split>('who');
  const [menu, setMenu] = useState(false);
  const days = stats?.money.days ?? [];
  const labels = days.map((d) => d.day);
  const chatDay = new Map((chat?.days ?? []).map((d) => [d.date, d]));
  const teamDay = new Map((stats?.team_days ?? []).map((d) => [d.day, d]));
  const aiChats = labels.map((d) => chatDay.get(d)?.chats_activated ?? 0);
  const teamChats = labels.map((d) => (hasCrm ? teamDay.get(d)?.chats ?? 0 : 0));
  const effSplit: Split = metric === 'rev' ? split : 'who';
  const rate = metric === 'conv';

  const teamOff = !hasCrm;
  const who = (key: 'ai' | 'team' | 'unknown' | 'not_in_chat', values: number[]): Series => ({
    key, label: t(`sellers.${key}`), values,
    color: key === 'team' && teamOff ? 'var(--om-hover)' : SELLER_COLOR[key],
    hatch: (key === 'team' && teamOff) || key === 'not_in_chat',
  });

  let series: Series[];
  if (metric === 'rev' && effSplit === 'type') {
    const types = [...new Set(days.flatMap((d) => Object.keys(d.by_type)))].sort((a, b) => typeOrder(a) - typeOrder(b));
    series = types.map((ty) => ({ key: ty, label: t(`types.${ty}`, { defaultValue: ty }), color: typeColor(ty), values: days.map((d) => pick(d.by_type[ty], mode)) }));
  } else if (metric === 'rev') {
    series = (['ai', 'team', 'unknown', 'not_in_chat'] as const).map((k) => who(k, days.map((d) => (k === 'team' && teamOff ? 0 : pick(d.sellers[k], mode)))));
  } else if (metric === 'chats') {
    series = [who('ai', aiChats), who('team', teamChats)];
  } else if (metric === 'msgs') {
    series = [
      who('ai', labels.map((d) => { const c = chatDay.get(d); return (c?.messages_sent ?? 0) + (c?.follow_ups_sent ?? 0); })),
      who('team', labels.map((d) => (hasCrm ? teamDay.get(d)?.messages ?? 0 : 0))),
    ];
  } else if (metric === 'sold') {
    series = (['ai', 'team', 'unknown'] as const).map((k) => who(k, days.map((d) => (k === 'team' && teamOff ? 0 : d.ppv_sold[k]))));
  } else {
    // Chats that paid: payments the AI and Team made ÷ the chats they handled.
    series = [{
      key: 'all', label: t('chart.average'), color: SELLER_COLOR.ai,
      values: days.map((d, i) => ratio(d.payments.ai + (hasCrm ? d.payments.team : 0), (aiChats[i] ?? 0) + (teamChats[i] ?? 0)) * 100),
    }];
  }

  const totals = labels.map((_, i) => series.reduce((a, s) => a + (s.values[i] ?? 0), 0));
  const top = niceTop(Math.max(0, ...totals), rate);
  const money = metric === 'rev';
  const val = (v: number) => (money ? usd(v) : rate ? `${v.toFixed(1)}%` : fmt(v));
  const axis = (v: number) => (money ? compactUsd(v) : rate ? `${Math.round(v)}%` : compactNum(v));
  const avgRate = (() => {
    if (!rate) return 0;
    const pays = days.reduce((a, d) => a + d.payments.ai + (hasCrm ? d.payments.team : 0), 0);
    const ch = aiChats.reduce((a, b) => a + b, 0) + teamChats.reduce((a, b) => a + b, 0);
    return ratio(pays, ch) * 100;
  })();

  const metrics: { id: Metric; label: string; note: string }[] = [
    { id: 'rev', label: t('chart.rev'), note: t('chart.revNote') },
    { id: 'chats', label: t('chart.chats'), note: t('chart.chatsNote') },
    { id: 'msgs', label: t('chart.msgs'), note: t('chart.msgsNote') },
    { id: 'sold', label: t('chart.sold'), note: t('chart.soldNote') },
    { id: 'conv', label: t('chart.conv'), note: t('chart.convNote') },
  ];
  const current = metrics.find((x) => x.id === metric)!;
  const gap = labels.length > 31 ? 1 : labels.length > 14 ? 4 : 12;
  const every = Math.ceil(labels.length / 10);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setMenu((o) => !o)} className="cpt-hover-border" style={{ height: 28, padding: '0 8px 0 10px', display: 'flex', alignItems: 'center', gap: 6, background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(600, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {t('chart.byDay', { metric: current.label })}
            <Icon name="chevron-down" size={12} color={C.sub} />
          </button>
          {menu && (
            <>
              <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
              <div style={{ position: 'absolute', top: 32, left: 0, zIndex: 6, width: 220, padding: 4, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column' }}>
                {metrics.map((x) => (
                  <button key={x.id} type="button" className="cpt-hover-row" onClick={() => { setMetric(x.id); setMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32, padding: '4px 8px', border: 0, borderRadius: 6, background: x.id === metric ? C.tint16 : 'transparent', color: x.id === metric ? C.text : 'var(--om-body)', font: f(500, 12.5), textAlign: 'left', cursor: 'pointer' }}>
                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span>{x.label}</span>
                      <span style={{ font: f(400, 11), color: C.muted }}>{x.note}</span>
                    </span>
                    {x.id === metric && <Icon name="check" size={13} color={C.purpleLt} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <Seg
          size="sm"
          value={effSplit}
          onChange={setSplit}
          options={[
            { id: 'who', label: t('chart.who'), title: t('chart.whoTip') },
            { id: 'type', label: t('chart.type'), title: metric === 'rev' ? t('chart.typeTip') : t('chart.typeRevOnly'), disabled: metric !== 'rev' },
          ]}
        />
        <span style={{ flex: 1 }} />
        <span style={{ font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{money || metric === 'sold' || rate ? t('chart.utcDays') : t('chart.kyivDays')}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr)', gap: 6, flex: 1, minHeight: 170 }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', font: f(500, 10.5), color: C.muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums', margin: '-6px 0' }}>
          {[top, (top * 2) / 3, top / 3, 0].map((v, i) => <span key={i}>{axis(v)}</span>)}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap, padding: '0 4px', borderBottom: `1px solid ${C.borderHi}`, backgroundImage: `linear-gradient(${C.divider} 1px, transparent 1px)`, backgroundSize: '100% 33.333%' }}>
          {labels.map((d, i) => {
            const tip = `${shortDay(d)} · ${val(totals[i] ?? 0)}${rate || series.length < 2 ? '' : `\n${series.map((s) => `${s.label} ${s.key === 'team' && teamOff ? '—' : val(s.values[i] ?? 0)}`).join('\n')}`}`;
            return (
              <div key={d} title={tip} className="stx-bar" style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column-reverse', gap: 2 }}>
                {series.map((s) => {
                  const h = ((s.values[i] ?? 0) / top) * 100;
                  return h > 0 ? <span key={s.key} style={{ height: `calc(${h}% - 2px)`, minHeight: 2, background: s.color, backgroundImage: s.hatch ? HATCH : undefined, borderRadius: 2 }} /> : null;
                })}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '40px minmax(0,1fr)', gap: 6, marginTop: -6 }}>
        <span />
        <div style={{ display: 'flex', gap, padding: '0 4px' }}>
          {labels.map((d, i) => <span key={d} style={{ flex: 1, textAlign: 'center', font: f(500, 11), color: C.muted, overflow: 'hidden', whiteSpace: 'nowrap' }}>{i % every === 0 ? shortDay(d) : ''}</span>)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap', paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
        {rate ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.sub }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: SELLER_COLOR.ai }} />
            {t('chart.average')}
            <span style={{ color: C.text, fontWeight: 600 }}>{avgRate.toFixed(1)}%</span>
          </span>
        ) : series.map((s) => (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, backgroundImage: s.hatch ? HATCH : undefined }} />
            {s.label}
            <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.key === 'team' && teamOff ? '—' : val(s.values.reduce((a, b) => a + b, 0))}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
